import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";

const DEFAULT_PAGE_SIZE = 250;

const emptyTableData = {
  columns: [],
  rows: [],
  foreign_keys: {},
  row_count: 0,
  offset: 0,
  limit: DEFAULT_PAGE_SIZE,
  has_more: false,
};

const arrangeColumns = (columns) => {
  if (!Array.isArray(columns) || columns.length === 0) {
    return [];
  }
  const technicalColumns = ["created_at", "updated_at", "deleted_at"];
  const contentColumns = columns.filter((column) => !technicalColumns.includes(column));
  const trailingColumns = columns
    .filter((column) => technicalColumns.includes(column))
    .sort((left, right) => technicalColumns.indexOf(left) - technicalColumns.indexOf(right));
  return [...contentColumns, ...trailingColumns];
};

const mergeForeignKeys = (sourceData, fallbackData) => {
  if (!sourceData) {
    return fallbackData;
  }
  const mergedForeignKeys = {
    ...(fallbackData?.foreign_keys || {}),
    ...(sourceData?.foreign_keys || {}),
  };
  Object.keys(mergedForeignKeys).forEach((column) => {
    if (fallbackData?.foreign_keys?.[column] && sourceData?.foreign_keys?.[column]) {
      mergedForeignKeys[column] = {
        ...fallbackData.foreign_keys[column],
        ...sourceData.foreign_keys[column],
      };
    }
  });
  return {
    ...sourceData,
    foreign_keys: mergedForeignKeys,
  };
};

const normalizeRows = (rawData) => {
  if (!rawData?.rows) {
    return {
      ...emptyTableData,
    };
  }
  const arrangedColumns = arrangeColumns(rawData.columns || []);
  if (rawData.rows.length === 0) {
    return {
      ...rawData,
      columns: arrangedColumns,
      rows: [],
    };
  }
  if (Array.isArray(rawData.rows[0])) {
    return {
      ...rawData,
      columns: arrangedColumns,
      rows: rawData.rows.map((row) =>
        arrangedColumns.map((column) => {
          const originalIndex = rawData.columns.indexOf(column);
          return row[originalIndex];
        })
      ),
    };
  }
  return {
    ...rawData,
    columns: arrangedColumns,
    rows: rawData.rows.map((row) => arrangedColumns.map((column) => row[column])),
  };
};

function useTablePageData(tableName) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { fetchWithAuth } = useAuth();
  const [tableData, setTableData] = useState(emptyTableData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [uploading, setUploading] = useState(false);
  const [uploadingPreview, setUploadingPreview] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [progress, setProgress] = useState({
    processed: 0,
    total: 0,
    inserted: 0,
    updated: 0,
    errors: 0,
    error_messages: [],
  });

  const fetchPage = useCallback(
    async (pageNumber = 1) => {
      if (!tableName) {
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const offset = (pageNumber - 1) * DEFAULT_PAGE_SIZE;
        const response = await fetchWithAuth(
          `/tables/${tableName}/sample?limit=${DEFAULT_PAGE_SIZE}&offset=${offset}`
        );
        if (!response.ok) {
          throw new Error("Failed to load table data");
        }
        const responseData = await response.json();
        setTableData({
          ...emptyTableData,
          ...responseData,
        });
        setCurrentPage(pageNumber);
      } catch (fetchError) {
        setError(fetchError.message);
      } finally {
        setLoading(false);
      }
    },
    [fetchWithAuth, tableName]
  );

  const runSearch = useCallback(
    async (term) => {
      if (!tableName) {
        return;
      }
      const trimmedTerm = term.trim();
      if (trimmedTerm.length < 3) {
        setSearchResults(null);
        setSearchLoading(false);
        return;
      }
      setSearchLoading(true);
      try {
        const response = await fetchWithAuth(
          `/tables/${tableName}/search?q=${encodeURIComponent(trimmedTerm)}`
        );
        if (response.status === 404) {
          setSearchResults({ columns: [], rows: [], foreign_keys: {} });
          setError(null);
          return;
        }
        if (!response.ok) {
          throw new Error("Search failed");
        }
        const results = await response.json();
        setSearchResults(results);
        setError(null);
      } catch (searchError) {
        setError(searchError.message);
      } finally {
        setSearchLoading(false);
      }
    },
    [fetchWithAuth, tableName]
  );

  useEffect(() => {
    setTableData(emptyTableData);
    setError(null);
    setSearchTerm("");
    setSearchResults(null);
    setUploadResult(null);
    setCurrentPage(1);
    if (tableName) {
      fetchPage(1);
    }
  }, [fetchPage, tableName]);

  useEffect(() => {
    if (!tableName) {
      return undefined;
    }
    if (searchTerm.trim().length === 0) {
      setSearchResults(null);
      setSearchLoading(false);
      return undefined;
    }
    const timeoutId = window.setTimeout(() => {
      runSearch(searchTerm);
    }, 300);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [runSearch, searchTerm, tableName]);

  const refresh = useCallback(async () => {
    if (!tableName) {
      return;
    }
    if (searchTerm.trim().length >= 3) {
      await runSearch(searchTerm);
      return;
    }
    await fetchPage(currentPage);
  }, [currentPage, fetchPage, runSearch, searchTerm, tableName]);

  const goToPage = useCallback(
    async (pageNumber) => {
      if (!tableName || pageNumber < 1) {
        return;
      }
      await fetchPage(pageNumber);
    },
    [fetchPage, tableName]
  );

  const saveRecord = useCallback(
    async (payload, meta) => {
      const url = meta.isNewRecord
        ? `/tables/${tableName}/rows`
        : `/tables/${tableName}/rows/${meta.recordId}`;
      const response = await fetchWithAuth(url, {
        method: meta.isNewRecord ? "POST" : "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(
          errorData?.detail ||
            `Failed to save record: ${response.status} ${response.statusText}`
        );
      }
      const savedData = await response.json();
      await refresh();
      return savedData;
    },
    [fetchWithAuth, refresh, tableName]
  );

  const deleteRecord = useCallback(
    async (recordId) => {
      const response = await fetchWithAuth(`/tables/${tableName}/rows/${recordId}`, {
        method: "DELETE",
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(
          errorData?.detail ||
            `Failed to delete record: ${response.status} ${response.statusText}`
        );
      }
      await refresh();
    },
    [fetchWithAuth, refresh, tableName]
  );

  const exportExcel = useCallback(
    async (template = false) => {
      const response = await fetchWithAuth(
        `/tables/${tableName}/export${template ? "-template" : ""}`,
        {
          method: "GET",
        }
      );
      if (!response.ok) {
        throw new Error(`Export failed with status ${response.status}`);
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.style.display = "none";
      anchor.href = url;
      anchor.download = `${tableName}_${template ? "template" : "export"}.xlsx`;
      document.body.appendChild(anchor);
      anchor.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(anchor);
    },
    [fetchWithAuth, tableName]
  );

  const importFile = useCallback(
    async (file) => {
      if (!file || !tableName) {
        return;
      }
      setUploading(true);
      setUploadResult(null);
      setProgress({
        processed: 0,
        total: 0,
        inserted: 0,
        updated: 0,
        errors: 0,
        error_messages: [],
      });
      try {
        const formData = new FormData();
        formData.append("file", file);
        const response = await fetchWithAuth(`/tables/${tableName}/import/stream`, {
          method: "POST",
          body: formData,
        });
        if (!response.ok) {
          throw new Error(`HTTP error ${response.status}`);
        }
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        while (true) {
          const { value, done } = await reader.read();
          if (done) {
            break;
          }
          const text = decoder.decode(value);
          const lines = text.split("\n\n");
          for (const line of lines) {
            if (!line.startsWith("data: ")) {
              continue;
            }
            const payload = JSON.parse(line.slice(6));
            if (payload.type === "progress") {
              setProgress(payload);
              continue;
            }
            if (payload.type === "complete") {
              setProgress(payload);
              setUploadResult({
                status: "success",
                inserted: payload.inserted,
                updated: payload.updated,
                errors: payload.errors,
                error_messages: payload.error_messages,
              });
              await refresh();
              continue;
            }
            if (payload.type === "error") {
              setUploadResult({
                status: "error",
                message: payload.message,
              });
            }
          }
        }
      } catch (uploadError) {
        setUploadResult({
          status: "error",
          message: uploadError.message,
        });
      } finally {
        setUploading(false);
      }
    },
    [fetchWithAuth, refresh, tableName]
  );

  const previewImport = useCallback(
    async (file) => {
      if (!file || !tableName) {
        return;
      }
      setUploadingPreview(true);
      setUploadResult(null);
      try {
        const formData = new FormData();
        formData.append("file", file);
        const response = await fetchWithAuth(`/tables/${tableName}/preview-import`, {
          method: "POST",
          body: formData,
        });
        if (!response.ok) {
          const errorData = await response.json().catch(() => null);
          throw new Error(errorData?.detail || "Preview import failed");
        }
        const responseData = await response.json();
        dispatch({
          type: "LOAD_PREVIEW_IMPORT",
          payload: {
            fileName: file.name,
            data: {
              tableName,
              rows: responseData,
            },
          },
        });
        navigate(`/preview-import/${encodeURIComponent(file.name)}`);
      } catch (previewError) {
        setUploadResult({
          status: "error",
          message: previewError.message,
        });
      } finally {
        setUploadingPreview(false);
      }
    },
    [dispatch, fetchWithAuth, navigate, tableName]
  );

  const mergedData = useMemo(
    () => (searchResults ? mergeForeignKeys(searchResults, tableData) : tableData),
    [searchResults, tableData]
  );

  const displayData = useMemo(() => normalizeRows(mergedData), [mergedData]);
  const isSearchActive = searchTerm.trim().length >= 3 && searchResults !== null;
  const pageCount = Math.max(1, Math.ceil((tableData.row_count || 0) / DEFAULT_PAGE_SIZE));

  return {
    pageSize: DEFAULT_PAGE_SIZE,
    tableData,
    displayData,
    loading,
    error,
    searchTerm,
    setSearchTerm,
    searchLoading,
    isSearchActive,
    currentPage,
    pageCount,
    goToPage,
    refresh,
    saveRecord,
    deleteRecord,
    exportExcel,
    importFile,
    previewImport,
    uploading,
    uploadingPreview,
    uploadResult,
    progress,
  };
}

export default useTablePageData;
